<script setup lang="ts">
import Swiper from 'swiper'
import 'swiper/css'
import type { Articles, Article } from '#shared/interfaces/pickup/article'
import { jsonParse } from '#shared/utils/jsonParse'
import { LOG_SOURCES } from '#shared/constant/log'

const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()
const showFlg = ref(false)

interface PickupInfo {
  post_id: number
}

// pickup の post_id 一覧を取得してから記事本体を引く2段構えの取得。
// 後段が前段の結果に依存するため useFetch では表現できず、useAsyncData の
// ハンドラ内に $fetch を2本まとめる（規約「複数 API の並列取得や可変長リクエスト」）
const { data: pickupArticles } = await useAsyncData<Array<Article>>(
  'pickup-articles',
  async () => {
    const pickupJson = await $fetch<string>('/api/s3/pickup', {
      cache: 'no-cache',
    })
    if (!pickupJson) {
      return []
    }
    // 受信元とのインターフェース定義に基づき pickup データは PickupInfo 配列が返る前提。
    // jsonParse の戻り値は any のため、null 判定に必要な null を含めてこの定数が持つべき期待のデータ構造へアサーションする。
    const parsedPickups = jsonParse(
      pickupJson,
      LOG_SOURCES.FETCH_PICKUP
    ) as Array<PickupInfo> | null
    if (parsedPickups === null) {
      return []
    }

    const postIdList = parsedPickups.map((pickup) => pickup.post_id)
    if (postIdList.length === 0) {
      return []
    }

    const result = await $fetch<Articles>('/api/goola/getSearch', {
      cache: 'no-cache',
      query: {
        category: 'ART',
        post_id: postIdList.join(','),
      },
    })
    if (!(result?.docs instanceof Array) || !result.docs.length) {
      return []
    }
    // pickup で指定された post_id の順序を保って記事を並べる
    return postIdList.flatMap((pickupPostId) => {
      const firstArticle = result.docs.filter(
        (article) => article.post_id === pickupPostId
      )[0]
      return firstArticle ? [firstArticle] : []
    })
  }
)
const articleList = computed<Array<Article>>(() => pickupArticles.value ?? [])

const sendMagazine = (magazineId: string, index: number) => {
  nuxtApp.$sendEventGaTag('CatalogPickupNo' + (index + 1) + 'Tap')
  window.location.href = config.public.MAGAZINE_URL + '/magazine/' + magazineId
}

const initPickupSwiper = () => {
  nextTick(() => {
    new Swiper('.swiper-pickup', {
      modules: [],
      slidesPerView: 2.3, // current view slide
      spaceBetween: 12, // space slide Between slide
      // Responsive breakpoints
      breakpoints: {
        // when window width is >= 320px
        320: {
          slidesPerView: 2.3,
          spaceBetween: 12,
        },
        420: {
          slidesPerView: 2.7,
          spaceBetween: 12,
        },
        // when window width is >= 480px
        480: {
          slidesPerView: 3,
          spaceBetween: 12,
        },
        // when window width is >= 640px
        640: {
          slidesPerView: 4,
          spaceBetween: 12,
        },
        // when window width is >= 640px
        768: {
          slidesPerView: 5,
          spaceBetween: 12,
        },
      },
    })
  })
}

onMounted(() => {
  initPickupSwiper()
  showFlg.value = true
})
</script>
<template>
  <div v-if="showFlg && articleList.length !== 0" class="row slide-pic">
    <div>
      <p class="fw-bold">ピックアップ</p>
    </div>
    <div class="swiper-box">
      <div class="swiper swiper-pickup">
        <div class="swiper-wrapper">
          <div
            v-for="(article, index) in articleList"
            :key="article.post_id"
            class="swiper-slide"
            @click="sendMagazine(String(article.post_id), index)"
          >
            <div class="slide">
              <ul>
                <li class="noPadding">
                  <a data-wpel-link="internal">
                    <img
                      class="pickup-img"
                      :src="article.thumbnail_path"
                      onerror="
                        this.src =
                          'https://img.goo-net.com/goo/usedcar/nophoto_big.jpg'
                      "
                      loading="eager"
                    />
                  </a>
                </li>
              </ul>
            </div>
            <div class="name">
              {{ article.title }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.slide-pic {
  padding-left: 0px;
}
.swiper-box {
  padding: 0 0 0 0;
}
.swiper-pickup {
  overflow: hidden;
  padding: 0 16px 0 16px;
}

.slide ul {
  margin: 0;
  padding: 0;
  border-radius: 10px;
  background: #fff;
  box-shadow: 1px 1px 3px 0 #ccc;
  text-align: left;
  margin-right: 7px;
  width: 136px;
  height: 100px;
}

.slide ul li {
  list-style: none;
  font-size: 12px;
  margin: 5px 0;
  line-height: 1;
  padding: 0 10px;
}

.slide ul li img {
  width: 136px;
  height: 100px;
  display: block;
  border-radius: 10px;
}

.slide ul li.noPadding {
  padding: 0;
  margin-top: 0;
}

.swiper-slide .name {
  font-size: 12px;
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 8px;
  width: 136px;
}

.fw-bold {
  font-size: 20px;
  margin-top: 32px;
  margin-bottom: 20px;
}

.pickup-img {
  vertical-align: middle;
  object-fit: cover;
}
</style>
